/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderNavigationService } from '@gh/builder-shared';
import { BuilderPermission, JobStageListItemStatus } from '@gh/builder-shared-data';
import {
  createDefaultGridModel,
  EntityInputDialogComponent,
  EntityInputDialogConfig,
  ROW_DELETED_CLASS,
} from '@gh/common-internal-shared';
import { createDialogRoute, getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  GridDefinition,
  GridDefinitionFactory,
  rowDragMoveEventArrayFactory,
} from '@gh/core-ui-grid';
import { filter$, switchMap$ } from '@gh/rx/operators';

import { assign } from 'lodash';

import { JobStageListBusinessService } from '../../resources/job-stage-list-business.service';
import {
  getJobStagesListItemId,
  JobStageListItem,
  jobStagesArr,
} from '../../resources/job-stages.model';

export const jobStageListItemDialog = createDialogRoute({
  component: EntityInputDialogComponent,
  init: JobStageListBusinessService,
});

@Component({
  standalone: false,
  selector: 'gh-job-stages-list-tab',
  templateUrl: 'job-stage-list-tab.component.html',
  styleUrls: ['job-stage-list-tab.component.scss'],
})
export class JobStageListTabComponent implements OnInit {
  stagesList: JobStageListItem[];
  definition: GridDefinition;
  showDeleted: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private waitingService: WaitingService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private navigationService: BuilderNavigationService,
    private businessService: JobStageListBusinessService,
    private gridDefinitionFactory: GridDefinitionFactory,
  ) {
  }

  ngOnInit(): void {
    assign(this, getRouteInitData(this.route));
  }

  isActive(item: JobStageListItem): boolean {
    return item.status === JobStageListItemStatus.Active;
  }

  onDropStage(event: CdkDragDrop<JobStageListItem>): void {
    moveItemInArray(this.stagesList, event.previousIndex, event.currentIndex);
    this.businessService.updateJobStages(this.stagesList).pipe(
      (this.waitingService.operator()))
      .subscribe(
        (stagesList: JobStageListItem[]) => {
          this.stagesList = stagesList;
        },
        (error) => this.notificationService.processAll(error),
      );
  }

  onToggleShowDeleted(): void {
    this.showDeleted = !this.showDeleted;
    this.load();
  }

  onDelete(item: JobStageListItem): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_stage_delete')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.businessService.delete(item).pipe((this.waitingService.operator<JobStageListItem>()))))
      .subscribe(() => {
        this.load();
      },         this.notificationService.processAll);
  }

  onUndelete(item: JobStageListItem): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_stage_undelete')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.businessService.undelete(item).pipe((this.waitingService.operator<JobStageListItem>()))))
      .subscribe(() => {
        this.load();
      },         this.notificationService.processAll);
  }

  onCreate(): void {
    this.dialogService.openWithInput(jobStageListItemDialog, <EntityInputDialogConfig<JobStageListItem>>{
      titleId: 'lbl_job_stages',
      messageId: 'lbl_job_stage_create_edit',
      placeholderId: 'lbl_description',
      field: 'name',
      required: true,
      maxLength: 128,
      permissionEdit: BuilderPermission.Setup.JobAdmin.JobStages.CREATE,
      showTextArea: true,
      onSave: (item: JobStageListItem) => this.businessService.create(item),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((item: any) => {
        this.stagesList = jobStagesArr.append(this.stagesList, item);
        this.notificationService.success('msg_created_successfully');
      },         this.notificationService.processAll);
  }

  onEdit(item: JobStageListItem): void {
    this.dialogService.openWithInput(jobStageListItemDialog, <EntityInputDialogConfig<JobStageListItem>>{
      titleId: 'lbl_job_stages',
      messageId: 'lbl_job_stage_create_edit',
      placeholderId: 'lbl_description',
      field: 'name',
      required: true,
      maxLength: 128,
      permissionEdit: BuilderPermission.Setup.JobAdmin.JobStages.EDIT,
      showTextArea: true,
      id: item.id,
      onSave: (jobStagesItem: JobStageListItem) => this.businessService.update(jobStagesItem),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((jobStageListItem: any) => {
        this.stagesList = jobStagesArr.update(this.stagesList,
                                              getJobStagesListItemId(jobStageListItem),
                                              jobStageListItem);
        this.notificationService.success('msg_saved_successfully');
      },         this.notificationService.processAll);
  }

  load(): void {
    this.businessService.findAll({ showDeleted: this.showDeleted })
      .subscribe((stages: JobStageListItem[]) => {
        this.stagesList = stages;
      });
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      label: label ? label : 'lbl_job_stages',
      entityType: entityType ? entityType : 'JOB_STAGE',
      entityId: entityId ? entityId : void 0,
    });
  }

  private buildGridDefinition(): GridDefinition {
    const model = createDefaultGridModel({
      getId: getJobStagesListItemId,
    });
    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [{
        rowDrag: true,
        field: 'name',
        width: 200,
      }],
      options: <any>{
        animateRows: true,
        rowClassRules: {
          [ROW_DELETED_CLASS]: ({ data }: { data: JobStageListItem }) =>
            data.status === JobStageListItemStatus.Inactive,
        },
        onRowDragMove: rowDragMoveEventArrayFactory(this.moveStages.bind(this)),
        onRowDragEnd: this.updateJobStages.bind(this),
      },
    });
  }

  private updateJobStages(): void {
    this.businessService.updateJobStages(this.stagesList).pipe(
      (this.waitingService.operator()))
      .subscribe((stages: JobStageListItem[]) => {
        this.stagesList = stages;
      },         this.notificationService.processAll);
  }

  private moveStages(from: JobStageListItem, to: JobStageListItem): void {
    const fromIndex = jobStagesArr.indexOf(this.stagesList, getJobStagesListItemId(from));
    const toIndex = jobStagesArr.indexOf(this.stagesList, getJobStagesListItemId(to));

    this.stagesList = jobStagesArr.move(this.stagesList, fromIndex, toIndex);
  }
}
